import { useState } from "react";
import { PaperPlaneTilt, EnvelopeSimple, CheckCircle } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

/**
 * v3.2.41 · "Get in touch" landing section.
 * Anonymous form → POST /api/contact. Matches the industrial-cyan
 * dark aesthetic (per /app/design_guidelines.json) and slots in
 * between the Trust section and the Footer CTA on Landing.jsx.
 */
export default function ContactSection() {
  const [name, setName]       = useState("");
  const [email, setEmail]     = useState("");
  const [message, setMessage] = useState("");
  const [website, setWebsite] = useState("");   // v3.2.57 · honeypot
  const [busy, setBusy]       = useState(false);
  const [sent, setSent]       = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (busy || sent) return;
    if (message.trim().length < 10) {
      toast.error("Please share a bit more (at least 10 characters).");
      return;
    }
    setBusy(true);
    try {
      await api.post("/contact", {
        name: name.trim(),
        email: email.trim(),
        message: message.trim(),
        website,                             // honeypot echo
      });
      setSent(true);
      toast.success("Thanks! We've sent a confirmation to your email.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Send failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="relative border-b border-white/10 bg-[#0A0C10]" data-testid="contact-section">
      <div className="absolute inset-0 bp-grid-strong opacity-20 pointer-events-none" />
      <div className="relative max-w-6xl mx-auto px-6 md:px-10 py-24 md:py-28 grid md:grid-cols-2 gap-12 md:gap-16 items-start">
        {/* Left: pitch */}
        <div>
          <span className="inline-flex items-center gap-2 border border-[#00E5FF]/30 bg-[#00E5FF]/5 px-3 py-1
                            font-mono text-[10px] uppercase tracking-[0.25em] text-[#00E5FF]"
                data-testid="contact-tag">
            <EnvelopeSimple size={12} weight="fill" /> Get in touch
          </span>
          <h2 className="mt-6 font-display text-4xl sm:text-5xl md:text-6xl font-black tracking-tighter leading-[0.95]"
              data-testid="contact-headline">
            Questions, ideas,<br />or a <span className="text-[#00E5FF]">deal to suggest?</span>
          </h2>
          <p className="mt-6 text-lg text-gray-400 max-w-lg leading-relaxed">
            Drop us a line and the EEngDeS team will get back to you.
            Whether it's a partnership, a bug, or a workflow you'd love
            to see on the platform. We're listening.
          </p>
          <div className="mt-8 flex items-center gap-3 font-mono text-xs text-gray-500 uppercase tracking-widest">
            <div className="w-8 h-px bg-[#00E5FF]/40" />
            <span>reply within 1 business day</span>
          </div>
        </div>

        {/* Right: form */}
        <form
          onSubmit={submit}
          className="relative border border-white/10 bg-black/40 p-6 md:p-8 backdrop-blur-sm"
          data-testid="contact-form"
        >
          {sent && (
            <div className="absolute inset-0 z-10 bg-[#0A0C10]/95 flex flex-col items-center justify-center text-center px-6"
                 data-testid="contact-form-sent">
              <CheckCircle size={54} weight="fill" className="text-emerald-400" />
              <div className="mt-4 font-display text-2xl font-black">Message received</div>
              <div className="mt-2 text-sm text-gray-400 max-w-sm">
                We just emailed a confirmation to <span className="font-mono text-white">{email || "you"}</span>.
                A human will reply from <span className="font-mono text-[#00E5FF]">admin@eengdes.com</span> shortly.
              </div>
            </div>
          )}

          <div className="space-y-5">
            {/* v3.2.57 · Honeypot — visually hidden, aria-hidden, off tab order.
                Real users never fill it; bots that auto-fill every input
                trip the silent drop on the backend. */}
            <div aria-hidden="true"
                  style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}>
              <label htmlFor="contact-website">Website (leave empty)</label>
              <input id="contact-website" type="text" tabIndex={-1} autoComplete="off"
                value={website} onChange={(e) => setWebsite(e.target.value)} />
            </div>

            <div>
              <label htmlFor="contact-name"
                     className="block font-mono text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-2">
                // your name
              </label>
              <input
                id="contact-name"
                required
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Jane Doe"
                maxLength={120}
                className="w-full bg-black/40 border border-white/10 focus:border-[#00E5FF] outline-none
                            px-4 py-3 text-white placeholder:text-gray-600 font-sans text-sm transition-colors"
                data-testid="contact-name-input"
              />
            </div>

            <div>
              <label htmlFor="contact-email"
                     className="block font-mono text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-2">
                // email
              </label>
              <input
                id="contact-email"
                required
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full bg-black/40 border border-white/10 focus:border-[#00E5FF] outline-none
                            px-4 py-3 text-white placeholder:text-gray-600 font-mono text-sm transition-colors"
                data-testid="contact-email-input"
              />
            </div>

            <div>
              <label htmlFor="contact-message"
                     className="block font-mono text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-2">
                // message
              </label>
              <textarea
                id="contact-message"
                required
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="How can we help?"
                rows={5}
                maxLength={4000}
                className="w-full bg-black/40 border border-white/10 focus:border-[#00E5FF] outline-none
                            px-4 py-3 text-white placeholder:text-gray-600 font-sans text-sm transition-colors resize-none"
                data-testid="contact-message-input"
              />
              <div className="mt-1 text-right font-mono text-[10px] text-gray-600">
                {message.length}/4000
              </div>
            </div>

            <button
              type="submit"
              disabled={busy}
              className="btn-primary w-full inline-flex items-center justify-center gap-2 px-6 py-4 text-sm disabled:opacity-50"
              data-testid="contact-submit-btn"
            >
              {busy ? "SENDING…" : (
                <>
                  <PaperPlaneTilt size={18} weight="fill" /> Send message
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
